import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import {
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    TextField,
    Typography,
} from '@material-ui/core';
import { notify } from 'utils';
import { USER_ROLES } from 'utils/constants';
import { ProjectServices } from '../../../../Services/ProjectServices';

const projectServices = new ProjectServices();

const SAVE_FAILED_MESSAGE = 'Learning access could not be saved.';
const ALLOCATION_FAILED_MESSAGE = 'The seat allocation could not be saved.';
const FREEMIUM_MAX_MONTHS = 6;
const LEARNING_HUB_MONTHS = 12;

const STATE_LABELS = {
    not_configured: 'Not configured',
    scheduled: 'Scheduled',
    active: 'Active',
    expired: 'Expired',
};

const ALLOCATION_STATUS = {
    active: 'Active',
    locked: 'Locked',
    expired: 'Expired',
};

const useStyles = makeStyles((theme) => ({
    section: {
        marginTop: 20,
        padding: theme.spacing(2),
        border: '1px solid var(--dm-border, #e7ebf0)',
        borderRadius: 12,
        backgroundColor: 'var(--dm-row-alt, #f9fbfd)',
    },
    unavailable: {
        marginLeft: 6,
        padding: '1px 7px',
        borderRadius: 999,
        fontSize: '0.6875rem',
        fontWeight: 700,
        color: 'var(--dm-muted, #647184)',
        backgroundColor: 'var(--dm-border, #e7ebf0)',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        marginBottom: theme.spacing(1),
    },
    title: {
        fontSize: 15,
        fontWeight: 700,
        lineHeight: '18px',
        color: 'var(--dm-text-strong, #4D5766)',
    },
    optional: {
        fontSize: 13,
        fontWeight: 500,
        color: 'var(--dm-muted, #647184)',
    },
    chip: {
        fontWeight: 600,
    },
    chipActive: {
        backgroundColor: '#e3f6e5',
        color: '#06781a',
    },
    chipScheduled: {
        backgroundColor: '#e8f0fc',
        color: '#2c5fb0',
    },
    chipExpired: {
        backgroundColor: '#fff1e6',
        color: '#9A3412',
    },
    statusText: {
        fontSize: 13,
        color: 'var(--dm-muted, #4D5766)',
        marginTop: theme.spacing(1),
    },
    summary: {
        marginTop: theme.spacing(1.5),
    },
    facts: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(112px, 1fr))',
        gap: 10,
        marginTop: 14,
    },
    fact: {
        padding: '10px 12px',
        borderRadius: 10,
        backgroundColor: 'var(--dm-row-alt, #f4f7fb)',
        border: '1px solid var(--dm-border, #e7ebf0)',
        minWidth: 0,
    },
    factLabel: {
        display: 'block',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: 'var(--dm-muted, #647184)',
    },
    factValue: {
        display: 'block',
        marginTop: 4,
        fontSize: 16,
        fontWeight: 700,
        lineHeight: 1.3,
        color: 'var(--dm-text-strong, #243044)',
    },
    seatRow: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        gap: 12,
        marginTop: 14,
    },
    seatField: {
        flex: '1 1 220px',
        maxWidth: 280,
    },
    seatFieldLocked: {
        '& .MuiOutlinedInput-root': {
            backgroundColor: 'var(--dm-row-alt, #f4f7fb)',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--dm-border, #e7ebf0)',
        },
        '& .MuiInputBase-input.Mui-disabled': {
            color: 'var(--dm-text-strong, #243044)',
            WebkitTextFillColor: 'var(--dm-text-strong, #243044)',
            cursor: 'default',
        },
    },
    seatActions: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        paddingTop: 4,
    },
    historyToggle: {
        marginTop: 4,
        paddingLeft: 0,
        textTransform: 'none',
        fontWeight: 600,
    },
    historyList: {
        position: 'relative',
        marginTop: 10,
        marginLeft: 6,
        paddingLeft: 16,
        borderLeft: '2px solid #d5deea',
    },
    historyCard: {
        position: 'relative',
        padding: '0 0 16px 4px',
        '&:last-child': {
            paddingBottom: 0,
        },
    },
    historyDot: {
        position: 'absolute',
        left: -22,
        top: 3,
        width: 10,
        height: 10,
        borderRadius: '50%',
        backgroundColor: '#fff',
        border: '2px solid #3d7ddd',
        boxSizing: 'border-box',
    },
    historyHead: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: 12,
    },
    historyTitle: {
        fontSize: 13,
        fontWeight: 700,
        color: 'var(--dm-text-strong, #243044)',
    },
    historyMeta: {
        fontSize: 12,
        color: 'var(--dm-muted, #647184)',
        textAlign: 'right',
    },
    historyText: {
        margin: '3px 0 0',
        fontSize: 13,
        lineHeight: 1.45,
        color: 'var(--dm-text-strong, #4D5766)',
    },
    historyReason: {
        marginTop: 2,
        fontSize: 13,
        lineHeight: 1.45,
        color: 'var(--dm-muted, #647184)',
    },
    message: {
        fontSize: 13,
        color: 'var(--dm-muted, #4D5766)',
        marginTop: theme.spacing(1),
    },
    allocation: {
        marginTop: theme.spacing(1.5),
        padding: theme.spacing(1.5),
        border: '1px solid var(--dm-border, #e7ebf0)',
        borderRadius: 10,
        backgroundColor: 'var(--dm-surface, #fff)',
    },
    allocationHead: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
    },
    meta: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(2),
        marginTop: theme.spacing(1),
        fontSize: 13,
        color: 'var(--dm-text-strong, #4D5766)',
    },
    history: {
        marginTop: theme.spacing(1),
        paddingTop: theme.spacing(1),
        borderTop: '1px solid var(--dm-border, #e7ebf0)',
    },
    historyItem: {
        marginTop: theme.spacing(1),
        fontSize: 13,
        color: 'var(--dm-text-strong, #4D5766)',
    },
    dialogLine: {
        fontSize: 14,
        marginBottom: theme.spacing(0.75),
        color: 'var(--dm-text-strong, #4D5766)',
    },
    addAllocation: {
        marginTop: theme.spacing(2),
    },
    actions: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: theme.spacing(2),
    },
    saveBtn: {
        padding: '8px 22px',
        fontSize: '0.875rem',
        borderRadius: 5,
        minWidth: 120,
    },
    planCardMuted: {
        opacity: 0.72,
    },
}));

export const FALLBACK_PLANS = [
    { code: 'freemium', label: 'Freemium', selectable: true, requires_seat_limit: false, max_duration_months: FREEMIUM_MAX_MONTHS, end_date_editable: true },
    { code: 'learning_hub', label: 'Learning Hub', selectable: true, requires_seat_limit: true },
    { code: 'learning_hub_plus', label: 'Learning Hub+', selectable: false, unavailable_reason: 'Not available yet.', requires_seat_limit: true },
];

export const EMPTY_ACCESS_FORM = { plan: '', access_start_date: '', access_end_date: '', seat_limit: '' };

export const planRequiresSeatLimit = (planCode, plans = []) => {
    const meta = (plans || []).find((plan) => plan.code === planCode);
    if (meta && typeof meta.requires_seat_limit === 'boolean') {
        return meta.requires_seat_limit;
    }
    return planCode === 'learning_hub' || planCode === 'learning_hub_plus';
};

/**
 * Freemium pickers read learning_access.freemium only.
 * Top-level dates are the active plan window (12 months while Hub is selected) and must not fill those pickers.
 * When the saved plan is already freemium and the nested window has no dates, the top-level dates are that freemium window.
 */
export const freemiumDatesFromAccess = (access) => {
    const nested = access?.freemium;
    if (nested && typeof nested === 'object') {
        const access_start_date = nested.access_start_date || '';
        const access_end_date = nested.access_end_date || '';
        if (access_start_date || access_end_date) {
            return { access_start_date, access_end_date };
        }
    }
    if (access?.plan === 'freemium') {
        return {
            access_start_date: access.access_start_date || '',
            access_end_date: access.access_end_date || '',
        };
    }
    return { access_start_date: '', access_end_date: '' };
};

/** Cap from the freemium window or the freemium plan entry. Never the Hub top-level max_duration_months. */
export const freemiumMaxMonths = (access, plans = []) => {
    const nested = Number(access?.freemium?.max_duration_months);
    if (Number.isFinite(nested) && nested > 0) return nested;
    const plan = (plans || []).find((item) => item?.code === 'freemium');
    const fromPlan = Number(plan?.max_duration_months);
    if (Number.isFinite(fromPlan) && fromPlan > 0) return fromPlan;
    return FREEMIUM_MAX_MONTHS;
};

/** First Hub save only. Once an allocation exists, further access is Add allocation. */
export const canSaveHubAccess = (access, allocations = access?.allocations) => {
    const rows = Array.isArray(allocations) ? allocations : [];
    if (rows.length > 0) return false;
    if (access?.can_save_hub_access === false) return false;
    return true;
};

/** Hub setup fields use the Hub window and seats, never the Freemium dates. */
export const hubSetupFromAccess = (access) => {
    const allocations = Array.isArray(access?.allocations) ? access.allocations : [];
    const hubOn = access?.plan === 'learning_hub' || allocations.length > 0;
    if (!hubOn) return { access_start_date: '', seat_limit: '' };
    const start = access?.access_start_date || allocations[0]?.access_start_date || '';
    const seat = access?.seat_limit ?? access?.seat_summary?.allocated ?? allocations[0]?.seat_limit;
    return {
        access_start_date: start || '',
        seat_limit: seat != null && seat !== '' ? String(seat) : '',
    };
};

/** Dates only mean something once a plan is configured; the API may send defaults otherwise. */
export const formFromAccess = (access) => {
    const configured = !!access?.plan && access?.access_state !== 'not_configured';
    const seatLimit = access?.seat_limit;
    const freemiumDates = access?.plan === 'freemium' ? freemiumDatesFromAccess(access) : null;
    return {
        plan: access?.plan || '',
        access_start_date: freemiumDates
            ? freemiumDates.access_start_date
            : (configured ? (access?.access_start_date || '') : ''),
        access_end_date: freemiumDates
            ? freemiumDates.access_end_date
            : (configured ? (access?.access_end_date || '') : ''),
        seat_limit: configured && seatLimit != null && seatLimit !== '' ? String(seatLimit) : '',
    };
};

export const addCalendarMonths = (isoDate, months) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate || '');
    if (!match) return '';
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const monthIndex = month - 1 + months;
    const lastDay = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
    const result = new Date(Date.UTC(year, monthIndex, Math.min(day, lastDay)));
    const y = result.getUTCFullYear();
    const m = String(result.getUTCMonth() + 1).padStart(2, '0');
    const d = String(result.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const wholeSeats = (value) => {
    if (value === '' || value == null) return null;
    const seats = Number(value);
    if (!Number.isInteger(seats)) return null;
    return seats;
};

export const validateLearningAccess = (form, plans = [], access = null) => {
    if (!form?.plan) {
        return form?.access_start_date || form?.access_end_date || form?.seat_limit
            ? 'Choose a Learning Hub plan to save these dates, or clear them.'
            : '';
    }
    if (!form.access_start_date) return 'Enter a valid access start date';
    if (planRequiresSeatLimit(form.plan, plans)) {
        const seats = wholeSeats(form.seat_limit);
        if (seats == null || seats < 1) return 'Enter a seat_limit of at least 1';
        return '';
    }
    if (!form.access_end_date) return 'Enter a valid access end date';
    if (form.access_end_date < form.access_start_date) {
        return 'Access end date must be on or after the start date';
    }
    const maxMonths = freemiumMaxMonths(access, plans);
    const maxEnd = addCalendarMonths(form.access_start_date, maxMonths);
    if (maxEnd && form.access_end_date > maxEnd) {
        const unit = Number(maxMonths) === 1 ? 'month' : 'months';
        return `Freemium access cannot be longer than ${maxMonths} ${unit}`;
    }
    return '';
};

export const buildLearningAccessPayload = (form, plans = []) => {
    if (planRequiresSeatLimit(form.plan, plans)) {
        return {
            plan: form.plan,
            access_start_date: form.access_start_date,
            seat_limit: Number(form.seat_limit),
        };
    }
    return {
        plan: form.plan,
        access_start_date: form.access_start_date,
        access_end_date: form.access_end_date,
    };
};

export const formatIsoDate = (value) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value || '');
    if (!match) return value ? String(value) : '';
    const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
};

const formatChangedAt = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const personName = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    return value.name || value.email || value.username || '';
};

export const historyEntries = (allocation) => (
    (Array.isArray(allocation?.history) ? allocation.history : []).map((row) => ({
        previous: row.previous_seats ?? row.previous_seat_limit ?? '—',
        next: row.new_seats ?? row.new_seat_limit ?? row.seat_limit ?? '—',
        utilized: row.utilized_seats ?? row.seats_used ?? '—',
        availableBefore: row.available_seats_before ?? row.seats_available_before ?? row.seats_remaining_before ?? '—',
        availableAfter: row.available_seats_after ?? row.seats_available_after ?? row.seats_remaining_after ?? '—',
        reason: row.reason || '',
        action: row.action || '',
        updatedBy: personName(row.updated_by),
        changedAt: formatChangedAt(row.changed_at),
    }))
);

const historyTitle = (entry) => {
    const action = String(entry.action || '').toLowerCase();
    if (action === 'created' || entry.previous === '—') return 'Initial allocation';
    if (action.includes('reduc')) return 'Seat reduction';
    return 'Seat change';
};

const FactTiles = ({ items, classes }) => (
    <div className={classes.facts}>
        {items.map((item) => (
            <div key={item.label} className={classes.fact}>
                <span className={classes.factLabel}>{item.label}</span>
                {' '}
                <span className={classes.factValue}>{item.value ?? '—'}</span>
            </div>
        ))}
    </div>
);

FactTiles.propTypes = {
    items: PropTypes.array.isRequired,
    classes: PropTypes.object.isRequired,
};

const allocationStatus = (allocation) => String(allocation?.status || '').toLowerCase();

const isLockedAllocation = (allocation) => allocationStatus(allocation) === 'locked' || allocation?.locked === true;

const canReduceAllocation = (allocation, allowed) => (
    allowed
    && allocationStatus(allocation) === 'active'
    && allocation?.can_reduce !== false
    && !isLockedAllocation(allocation)
);

const statusText = (access) => {
    switch (access?.access_state) {
        case 'scheduled':
            return 'Access has not started.';
        case 'active': {
            const days = access.access_days_remaining;
            return `${days} ${days === 1 ? 'day' : 'days'} remaining.`;
        }
        case 'expired':
            return 'Access has ended.';
        default:
            return '';
    }
};

/**
 * Saved mode (existing project): edits and saves through `project/learningaccess/{id}`.
 * Pending mode (new project): controlled by `value`/`onChange`; the project editor saves it
 * right after the project is created.
 */
const roleLocksAccess = () => {
    const role = localStorage.getItem('role');
    return !!role && role !== USER_ROLES.SUPER_ADMIN;
};

const mergeAccessResponse = (current, data, plans) => {
    if (!data || typeof data !== 'object') return current;
    const looksLikeAccess = data.plan != null
        || data.access_state
        || Array.isArray(data.allocations)
        || Array.isArray(data.available_plans);
    if (!looksLikeAccess && data.uuid && Array.isArray(current?.allocations)) {
        return {
            ...current,
            allocations: current.allocations.map((item) => (
                item.uuid === data.uuid ? { ...item, ...data } : item
            )),
        };
    }
    const merged = {
        ...current,
        ...data,
        available_plans: data.available_plans?.length ? data.available_plans : (current?.available_plans?.length ? current.available_plans : plans),
    };
    if (data.plan === 'freemium') {
        const nested = data.freemium && typeof data.freemium === 'object' ? data.freemium : {};
        const previous = current?.freemium && typeof current.freemium === 'object' ? current.freemium : {};
        merged.freemium = {
            ...previous,
            ...nested,
            plan: 'freemium',
            access_start_date: nested.access_start_date || data.access_start_date || previous.access_start_date || '',
            access_end_date: nested.access_end_date || data.access_end_date || previous.access_end_date || '',
        };
    }
    return merged;
};

const LearningAccessSection = ({
    projectId,
    learningAccess,
    pending = false,
    plans: pendingPlans,
    onChange,
    onSaved,
    readOnly = false,
}) => {
    const classes = useStyles();
    const [access, setAccess] = useState(learningAccess);
    const [freemiumForm, setFreemiumForm] = useState(() => freemiumDatesFromAccess(learningAccess));
    const [hubForm, setHubForm] = useState(() => hubSetupFromAccess(learningAccess));
    const [saving, setSaving] = useState('');
    const [seatDrafts, setSeatDrafts] = useState({});
    const [editingSeatId, setEditingSeatId] = useState(null);
    const [historyOpen, setHistoryOpen] = useState({});
    const [adding, setAdding] = useState(false);
    const [newAllocation, setNewAllocation] = useState({ access_start_date: '', seat_limit: '' });
    const [reduction, setReduction] = useState(null);
    const [reason, setReason] = useState('');

    useEffect(() => {
        setAccess(learningAccess);
        setFreemiumForm(freemiumDatesFromAccess(learningAccess));
        setHubForm(hubSetupFromAccess(learningAccess));
        setSeatDrafts({});
        setEditingSeatId(null);
        setAdding(false);
        setReduction(null);
        setReason('');
    }, [learningAccess]);

    const readOnlyAccess = readOnly || roleLocksAccess();
    const plans = pending
        ? (pendingPlans?.length ? pendingPlans : FALLBACK_PLANS)
        : (Array.isArray(access?.available_plans) ? access.available_plans : FALLBACK_PLANS);
    const allocations = pending || !Array.isArray(access?.allocations) ? [] : access.allocations;
    const savedFreemium = freemiumDatesFromAccess(access);
    const freemiumActive = !!savedFreemium.access_start_date;
    const hubActive = access?.plan === 'learning_hub' || allocations.length > 0;
    const sectionState = pending ? 'not_configured' : (freemiumActive || hubActive ? 'active' : 'not_configured');
    const chipClass = {
        active: classes.chipActive,
        scheduled: classes.chipScheduled,
        expired: classes.chipExpired,
    }[sectionState];
    const status = pending ? '' : statusText(access);
    const showHubSetup = allocations.length === 0;
    const canSaveHub = canSaveHubAccess(access, allocations);
    const showFreemiumSave = !readOnlyAccess && !pending;
    const showHubSave = showFreemiumSave && canSaveHub;
    const showAddAllocation = showFreemiumSave && !canSaveHub;
    const summary = allocations.length > 0 ? access?.seat_summary : null;
    const hubEndPreview = hubForm.access_start_date
        ? addCalendarMonths(hubForm.access_start_date, LEARNING_HUB_MONTHS)
        : '';
    const maxFreemiumMonths = freemiumMaxMonths(access, plans);
    const freemiumMaxEnd = freemiumForm.access_start_date
        ? addCalendarMonths(freemiumForm.access_start_date, maxFreemiumMonths)
        : '';
    const plusPlan = plans.find((plan) => plan.code === 'learning_hub_plus');
    const plusReason = plusPlan?.unavailable_reason || 'Not available yet.';
    const busy = !!saving;

    const applyResponse = async (res, failureMessage) => {
        if (res?.status && res.data) {
            const next = mergeAccessResponse(access, res.data, plans);
            setAccess(next);
            setFreemiumForm(freemiumDatesFromAccess(next));
            setHubForm(hubSetupFromAccess(next));
            setAdding(false);
            setNewAllocation({ access_start_date: '', seat_limit: '' });
            setReduction(null);
            setReason('');
            setSeatDrafts({});
            setEditingSeatId(null);
            notify('success', res.message);
            if (onSaved) await onSaved(next);
            return;
        }
        notify('error', res?.message || failureMessage);
    };

    const updateFreemium = (evt) => {
        const { name, value: nextValue } = evt.target;
        setFreemiumForm((current) => {
            const next = { ...current, [name]: nextValue };
            if (pending && onChange) {
                onChange({ plan: 'freemium', ...next, seat_limit: hubForm.seat_limit });
            }
            return next;
        });
    };

    const updateHub = (evt) => {
        const { name, value: nextValue } = evt.target;
        const key = name === 'hub_access_start_date' ? 'access_start_date' : name;
        setHubForm((current) => {
            const next = { ...current, [key]: nextValue };
            if (pending && onChange) {
                onChange({
                    plan: 'learning_hub',
                    access_start_date: next.access_start_date,
                    access_end_date: '',
                    seat_limit: next.seat_limit,
                });
            }
            return next;
        });
    };

    const savePlan = async (plan) => {
        if (plan === 'learning_hub' && !canSaveHubAccess(access, allocations)) return;
        const form = plan === 'freemium'
            ? { plan, ...freemiumForm, seat_limit: '' }
            : { plan, access_start_date: hubForm.access_start_date, access_end_date: '', seat_limit: hubForm.seat_limit };
        const error = validateLearningAccess(form, plans, access);
        if (error) {
            notify('error', error);
            return;
        }
        setSaving(plan);
        try {
            const res = await projectServices.saveLearningAccess(
                projectId,
                buildLearningAccessPayload(form, plans)
            );
            await applyResponse(res, SAVE_FAILED_MESSAGE);
        } finally {
            setSaving('');
        }
    };

    const openReduction = (allocation) => {
        const nextLimit = wholeSeats(seatDrafts[allocation.uuid] ?? allocation.seat_limit);
        const minimum = Number(allocation.minimum_seats);
        const current = Number(allocation.seat_limit);
        if (nextLimit == null || nextLimit < 1) {
            notify('error', 'Enter a whole number of seats');
            return;
        }
        if (Number.isFinite(minimum) && nextLimit < minimum) {
            notify('error', `Seats cannot go below ${minimum}`);
            return;
        }
        if (nextLimit > current) {
            notify('error', 'More seats are a new allocation. Use Add allocation.');
            return;
        }
        if (nextLimit === current) return;
        setReason('');
        setReduction({ allocation, seat_limit: nextLimit });
    };

    const confirmReduction = async () => {
        const trimmed = reason.trim();
        if (!trimmed || !reduction) return;
        setSaving('allocation');
        try {
            const res = await projectServices.updateLearningAllocation(
                projectId,
                reduction.allocation.uuid,
                { seat_limit: reduction.seat_limit, reason: trimmed }
            );
            await applyResponse(res, ALLOCATION_FAILED_MESSAGE);
        } finally {
            setSaving('');
        }
    };

    const saveNewAllocation = async () => {
        if (!newAllocation.access_start_date) {
            notify('error', 'Enter a valid access start date');
            return;
        }
        const seats = wholeSeats(newAllocation.seat_limit);
        if (seats == null || seats < 1) {
            notify('error', 'Enter a seat_limit of at least 1');
            return;
        }
        setSaving('allocation');
        try {
            const res = await projectServices.addLearningAllocation(projectId, {
                plan: 'learning_hub',
                access_start_date: newAllocation.access_start_date,
                seat_limit: seats,
            });
            await applyResponse(res, ALLOCATION_FAILED_MESSAGE);
        } finally {
            setSaving('');
        }
    };

    const newAllocationEnd = newAllocation.access_start_date
        ? addCalendarMonths(newAllocation.access_start_date, LEARNING_HUB_MONTHS)
        : '';
    const remainingAfterReduction = reduction
        ? reduction.seat_limit - Number(reduction.allocation.seats_used || 0)
        : 0;

    return (
        <div className={classes.section}>
            <div className={classes.header}>
                <Typography className={classes.title}>Learning Hub access</Typography>
                <Typography className={classes.optional}>Optional</Typography>
                <Chip
                    size="small"
                    label={STATE_LABELS[sectionState] || sectionState}
                    className={`${classes.chip}${chipClass ? ` ${chipClass}` : ''}`}
                />
            </div>
            {status ? <Typography className={classes.statusText}>{status}</Typography> : null}
            <div className={classes.allocation}>
                <div className={classes.allocationHead}>
                    <Typography className={classes.title}>Freemium</Typography>
                    {freemiumActive ? (
                        <Chip size="small" label="Active" className={`${classes.chip} ${classes.chipActive}`} />
                    ) : null}
                </div>
                <Grid container spacing={3} style={{ marginTop: 4 }}>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            variant="outlined"
                            size="small"
                            fullWidth
                            type="date"
                            label="Access start date"
                            name="access_start_date"
                            value={freemiumForm.access_start_date}
                            onChange={readOnlyAccess ? undefined : updateFreemium}
                            disabled={readOnlyAccess}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            variant="outlined"
                            size="small"
                            fullWidth
                            type="date"
                            label="Access end date"
                            name="access_end_date"
                            value={freemiumForm.access_end_date}
                            onChange={readOnlyAccess ? undefined : updateFreemium}
                            disabled={readOnlyAccess}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{
                                min: freemiumForm.access_start_date || undefined,
                                max: freemiumMaxEnd || undefined,
                            }}
                            helperText={`Last day access is open. Max ${maxFreemiumMonths} ${Number(maxFreemiumMonths) === 1 ? 'month' : 'months'}.`}
                        />
                    </Grid>
                </Grid>
                {showFreemiumSave ? (
                    <div className={classes.actions}>
                        <Button
                            color="primary"
                            variant="outlined"
                            className={classes.saveBtn}
                            onClick={() => savePlan('freemium')}
                            disabled={busy}
                        >
                            {saving === 'freemium' ? <CircularProgress size={15} color="inherit" /> : 'Save Freemium access'}
                        </Button>
                    </div>
                ) : null}
            </div>
            <div className={classes.allocation}>
                <div className={classes.allocationHead}>
                    <Typography className={classes.title}>Learning Hub</Typography>
                    {hubActive ? (
                        <Chip size="small" label="Active" className={`${classes.chip} ${classes.chipActive}`} />
                    ) : null}
                </div>
                {readOnlyAccess && access?.seat_message ? (
                    <Typography className={classes.message}>{access.seat_message}</Typography>
                ) : null}
                {showHubSetup ? (
                    <Grid container spacing={3} style={{ marginTop: 4 }}>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                variant="outlined"
                                size="small"
                                fullWidth
                                type="date"
                                label="Access start date"
                                name="hub_access_start_date"
                                value={hubForm.access_start_date}
                                onChange={readOnlyAccess ? undefined : updateHub}
                                disabled={readOnlyAccess}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                variant="outlined"
                                size="small"
                                fullWidth
                                type="date"
                                label="Access end date"
                                name="hub_access_end_date"
                                value={hubEndPreview}
                                disabled
                                InputLabelProps={{ shrink: true }}
                                helperText="The end date is 12 months after the start date."
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                variant="outlined"
                                size="small"
                                fullWidth
                                type="number"
                                label="Seat limit"
                                name="seat_limit"
                                value={hubForm.seat_limit}
                                onChange={readOnlyAccess ? undefined : updateHub}
                                disabled={readOnlyAccess}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ min: 1, step: 1 }}
                                helperText="At least 1. The first save creates allocation 1."
                            />
                        </Grid>
                    </Grid>
                ) : null}
            {summary ? (
                <div className={classes.summary}>
                    <FactTiles
                        classes={classes}
                        items={[
                            { label: 'Allocated', value: summary.allocated },
                            { label: 'Utilized', value: summary.utilized },
                            { label: 'Available', value: summary.available },
                        ]}
                    />
                </div>
            ) : null}
            {allocations.map((allocation) => {
                const reduceable = canReduceAllocation(allocation, !readOnlyAccess && access?.can_edit_allocations === true);
                const editing = editingSeatId === allocation.uuid;
                const draft = seatDrafts[allocation.uuid] ?? String(allocation.seat_limit ?? '');
                const history = historyEntries(allocation);
                const statusKey = allocationStatus(allocation);
                const statusLabel = ALLOCATION_STATUS[statusKey] || allocation.status;
                const statusClass = {
                    active: classes.chipActive,
                    locked: classes.chipExpired,
                    expired: classes.chipExpired,
                }[statusKey];
                return (
                    <div key={allocation.uuid} className={classes.allocation}>
                        <div className={classes.allocationHead}>
                            <Typography className={classes.title}>Allocation</Typography>
                            {statusLabel ? (
                                <Chip
                                    size="small"
                                    label={statusLabel}
                                    className={`${classes.chip}${statusClass ? ` ${statusClass}` : ''}`}
                                />
                            ) : null}
                        </div>
                        <FactTiles
                            classes={classes}
                            items={[
                                { label: 'Start date', value: formatIsoDate(allocation.access_start_date) },
                                { label: 'End date', value: formatIsoDate(allocation.access_end_date) },
                                { label: 'Allocated', value: allocation.seat_limit },
                                { label: 'Utilized', value: allocation.seats_used },
                                { label: 'Available', value: allocation.seats_remaining },
                            ]}
                        />
                        {allocation.message ? (
                            <Typography className={classes.message}>{allocation.message}</Typography>
                        ) : null}
                        {reduceable ? (
                            <div className={classes.seatRow}>
                                <TextField
                                    className={`${classes.seatField}${editing ? '' : ` ${classes.seatFieldLocked}`}`}
                                    variant="outlined"
                                    size="small"
                                    type="number"
                                    label="Seat limit"
                                    name={`reduce_seat_limit_${allocation.uuid}`}
                                    value={draft}
                                    disabled={!editing}
                                    onChange={(event) => {
                                        const nextValue = event.target.value;
                                        setSeatDrafts((current) => ({
                                            ...current,
                                            [allocation.uuid]: nextValue,
                                        }));
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{
                                        min: allocation.minimum_seats || 1,
                                        max: allocation.seat_limit,
                                        step: 1,
                                        readOnly: !editing,
                                    }}
                                    helperText={editing
                                        ? `Lower this to ${allocation.minimum_seats} or more. More seats need a new allocation.`
                                        : 'Locked. Choose Reduce limits to change this allocation.'}
                                />
                                <div className={classes.seatActions}>
                                    {editing ? (
                                        <>
                                            <Button
                                                color="primary"
                                                variant="contained"
                                                onClick={() => openReduction(allocation)}
                                                disabled={busy}
                                            >
                                                Apply reduction
                                            </Button>
                                            <Button
                                                onClick={() => {
                                                    setEditingSeatId(null);
                                                    setSeatDrafts((current) => {
                                                        const next = { ...current };
                                                        delete next[allocation.uuid];
                                                        return next;
                                                    });
                                                }}
                                                disabled={busy}
                                            >
                                                Cancel edit
                                            </Button>
                                        </>
                                    ) : (
                                        <Button
                                            color="primary"
                                            variant="outlined"
                                            onClick={() => {
                                                setEditingSeatId(allocation.uuid);
                                                setSeatDrafts((current) => ({
                                                    ...current,
                                                    [allocation.uuid]: String(allocation.seat_limit ?? ''),
                                                }));
                                            }}
                                            disabled={busy}
                                        >
                                            Reduce limits
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ) : null}
                        <Button
                            className={classes.historyToggle}
                            color="primary"
                            onClick={() => setHistoryOpen((current) => ({
                                ...current,
                                [allocation.uuid]: !current[allocation.uuid],
                            }))}
                        >
                            {historyOpen[allocation.uuid] ? 'Hide allocation history' : 'View Allocation History'}
                        </Button>
                        {historyOpen[allocation.uuid] ? (
                            <div className={classes.historyList}>
                                {history.length ? history.map((entry, index) => (
                                    <div key={`${allocation.uuid}-history-${index}`} className={classes.historyCard}>
                                        <span className={classes.historyDot} />
                                        <div className={classes.historyHead}>
                                            <span className={classes.historyTitle}>{historyTitle(entry)}</span>
                                            {entry.changedAt ? <span className={classes.historyMeta}>{entry.changedAt}</span> : null}
                                        </div>
                                        {entry.updatedBy ? (
                                            <div className={classes.historyMeta}>{entry.updatedBy}</div>
                                        ) : null}
                                        <p className={classes.historyText}>
                                            Previous seats {entry.previous}. New seats {entry.next}. Utilized {entry.utilized}. Available before {entry.availableBefore}, after {entry.availableAfter}.
                                        </p>
                                        {entry.reason && entry.reason.toLowerCase() !== historyTitle(entry).toLowerCase() ? (
                                            <div className={classes.historyReason}>{entry.reason}</div>
                                        ) : null}
                                    </div>
                                )) : (
                                    <Typography className={classes.message}>No allocation history.</Typography>
                                )}
                            </div>
                        ) : null}
                    </div>
                );
            })}
            {showAddAllocation ? (
                <div className={classes.addAllocation}>
                    {adding ? (
                        <Grid container spacing={2} alignItems="flex-start">
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    type="date"
                                    label="Access start date"
                                    name="allocation_start_date"
                                    value={newAllocation.access_start_date}
                                    onChange={(event) => {
                                        const nextValue = event.target.value;
                                        setNewAllocation((current) => ({
                                            ...current,
                                            access_start_date: nextValue,
                                        }));
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    type="number"
                                    label="Seat limit"
                                    name="allocation_seat_limit"
                                    value={newAllocation.seat_limit}
                                    onChange={(event) => {
                                        const nextValue = event.target.value;
                                        setNewAllocation((current) => ({
                                            ...current,
                                            seat_limit: nextValue,
                                        }));
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ min: 1, step: 1 }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                {newAllocationEnd ? (
                                    <Typography className={classes.statusText}>
                                        Access ends {formatIsoDate(newAllocationEnd)}. The end date is 12 months after the start date.
                                    </Typography>
                                ) : null}
                                <Button color="primary" variant="outlined" onClick={saveNewAllocation} disabled={busy}>
                                    Save allocation
                                </Button>
                                <Button onClick={() => setAdding(false)} disabled={busy}>Cancel</Button>
                            </Grid>
                        </Grid>
                    ) : (
                        <Button color="primary" variant="outlined" onClick={() => setAdding(true)}>
                            Add allocation
                        </Button>
                    )}
                </div>
            ) : null}
                {showHubSave ? (
                    <div className={classes.actions}>
                        <Button
                            color="primary"
                            variant="outlined"
                            className={classes.saveBtn}
                            onClick={() => savePlan('learning_hub')}
                            disabled={busy}
                        >
                            {saving === 'learning_hub' ? <CircularProgress size={15} color="inherit" /> : 'Save Learning Hub access'}
                        </Button>
                    </div>
                ) : null}
            </div>
            <div className={`${classes.allocation} ${classes.planCardMuted}`}>
                <div className={classes.allocationHead}>
                    <Typography className={classes.title}>Learning Hub+</Typography>
                    <span className={classes.unavailable}>{plusReason}</span>
                </div>
            </div>
            {pending ? (
                <Typography className={classes.statusText}>
                    Saved together with the project when you click Create project.
                </Typography>
            ) : null}
            <Dialog
                open={!!reduction}
                onClose={() => { if (!saving) { setReduction(null); setReason(''); } }}
                aria-labelledby="confirm-seat-allocation-reduction"
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle id="confirm-seat-allocation-reduction">Confirm Seat Allocation Reduction</DialogTitle>
                <DialogContent>
                    {reduction ? (
                        <>
                            <Typography className={classes.dialogLine}>
                                Previously allocated seats: {reduction.allocation.seat_limit}
                            </Typography>
                            <Typography className={classes.dialogLine}>
                                Seats already utilized: {reduction.allocation.seats_used}
                            </Typography>
                            <Typography className={classes.dialogLine}>
                                New allocation: {reduction.seat_limit}
                            </Typography>
                            <Typography className={classes.dialogLine}>
                                Remaining available seats after reduction: {remainingAfterReduction}
                            </Typography>
                        </>
                    ) : null}
                    <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        multiline
                        rows={3}
                        label="Reason"
                        name="reduction_reason"
                        value={reason}
                        onChange={(event) => setReason(event.target.value)}
                        InputLabelProps={{ shrink: true }}
                        required
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setReduction(null); setReason(''); }} disabled={busy}>Cancel</Button>
                    <Button color="primary" variant="contained" onClick={confirmReduction} disabled={busy || !reason.trim()}>
                        Confirm Reduction
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

LearningAccessSection.propTypes = {
    projectId: PropTypes.string,
    learningAccess: PropTypes.object,
    pending: PropTypes.bool,
    plans: PropTypes.array,
    value: PropTypes.object,
    onChange: PropTypes.func,
    onSaved: PropTypes.func,
    readOnly: PropTypes.bool,
};

export default LearningAccessSection;
